import { ApolloError, type ApolloQueryResult, type ServerError, type ServerParseError } from '@apollo/client';
import { GraphQLError, type GraphQLFormattedError } from 'graphql';

import defaultErrorToJson from '@/features/error/defaultErrorToJson';
import { type TOptional } from '@/types/common';

import { type TErrorSerializer, type TSerializedError } from './types';

export const extractQueryResultError = <R = unknown>({ error, errors }: ApolloQueryResult<R>) =>
  apolloErrorToJson(error) || graphQLErrorsToJson(errors);

const apolloErrorToJson = (error: TOptional<ApolloError>): TOptional<TSerializedError> => {
  if (error) {
    const { cause, clientErrors, extraInfo, graphQLErrors, message, name, networkError, protocolErrors, stack } = error;
    return {
      cause:
        cause instanceof Error
          ? applySerializers(cause, [serverErrorToJson, serverParseErrorToJson, defaultErrorToJson])
          : cause,
      clientErrors: clientErrors?.map(defaultErrorToJson),
      extraInfo,
      graphQLErrors: graphQLErrorsToJson(graphQLErrors),
      message,
      name,
      networkError: applySerializers(networkError!, [serverErrorToJson, serverParseErrorToJson, defaultErrorToJson]),
      protocolErrors,
      stack,
    };
  }
};

export const graphQLErrorsToJson = (errors: TOptional<ReadonlyArray<GraphQLError | GraphQLFormattedError>>) => {
  if (errors?.length)
    return errors.map((err) =>
      (err as GraphQLError).toJSON ? (err as GraphQLError).toJSON() : (err as GraphQLFormattedError),
    );
};

export const applySerializers = (
  error: TOptional<Error>,
  serializers: TErrorSerializer[],
): TOptional<TSerializedError> => {
  for (const serializer of serializers) {
    const serialized = serializer(error);
    if (serialized) return serialized;
  }
};

export const serverErrorToJson = (error: TOptional<Error>): TOptional<TSerializedError> => {
  if (
    error instanceof Error &&
    (error?.name === 'ServerError' || ['response', 'result', 'statusCode'].every((field) => field in error))
  ) {
    const { cause, message, name, response, result, stack, statusCode } = error as ServerError;
    type R = {
      errors?: TOptional<
        Array<{
          extensions?: TOptional<{ stacktrace?: TOptional<string[]> }>;
        }>
      >;
    };
    (result as R)?.errors?.forEach((item) => {
      delete item?.extensions?.stacktrace;
    });
    return { cause, message, name, response: responseToJson(response), result, stack, statusCode };
  }
};

export const serverParseErrorToJson = (error: TOptional<Error>): TOptional<TSerializedError> => {
  if (
    error instanceof Error &&
    (error?.name === 'ServerParseError' || ['bodyText', 'response', 'statusCode'].every((field) => field in error))
  ) {
    const { bodyText, cause, message, name, response, stack, statusCode } = error as ServerParseError;
    return { bodyText, cause, message, name, response: responseToJson(response), stack, statusCode };
  }
};

export const responseToJson = (response: Response) => {
  const { headers: origHeaders, ok, redirected, status, statusText, type, url } = response || {};
  const headers = origHeaders ? Object.fromEntries(origHeaders.entries()) : undefined;
  return { headers, ok, redirected, status, statusText, type, url };
};

export default apolloErrorToJson;
